/** @file quadAt: the drawn part (impostor quad) under a viewport point, nearest first, as a drawable index. */
import type { Profile } from '@zilo/pose3d-format/types/profile';
import type { Camera, ImpostorList } from '@zilo/pose3d-render-core/contract';
import { corners } from '@zilo/pose3d-render-core/corners/corners';
import { IMPOSTOR_LAYOUT as L } from '@zilo/pose3d-render-core/layout/impostorLayout';
import { pictureQuadOf } from './pictureQuadOf';
import type { ViewportCtx } from './viewportCtx';

/** True when `p` lies inside the convex quad `q` (screen px, either winding). */
function inQuad(p: readonly [number, number], q: readonly (readonly [number, number])[]): boolean {
  let sign = 0;
  const at = (i: number): readonly [number, number] => q[i % 4] ?? [0, 0];
  for (let i = 0; i < 4; i++) {
    const a = at(i);
    const b = at(i + 1);
    const cross = (b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0]);
    if (cross === 0) continue;
    const s = cross > 0 ? 1 : -1;
    if (sign === 0) sign = s;
    else if (s !== sign) return false;
  }
  return sign !== 0;
}

/** The impostor whose drawn quad (`pictureQuadOf`, where the picture and its outline are) holds `p`, nearest first, or -1. */
function nearestImpostorAt(list: ImpostorList, count: number, camera: Camera, profile: Profile, p: readonly [number, number]): number {
  let best = -1;
  let bestDepth = Infinity;
  for (let i = 0; i < count; i++) {
    const c = corners(list, i, camera, profile);
    if (!inQuad(p, pictureQuadOf(c.corners).corners)) continue;
    const depth = (c.depths[0] + c.depths[1] + c.depths[2] + c.depths[3]) / 4 - (list[i * L.stride + L.zBias] ?? 0);
    if (depth < bestDepth) { bestDepth = depth; best = i; }
  }
  return best;
}

/**
 * The index in `puppet.drawables` of the nearest drawn impostor whose full tile quad contains `p` (target pixels),
 * or -1 when no part is under the point or nothing is drawn yet.
 */
export function quadAt(ctx: ViewportCtx, p: readonly [number, number]): number {
  const puppet = ctx.puppet();
  if (puppet === null || ctx.scene === null || ctx.profile === null) return -1;
  const state = puppet.instance.state();
  const i = nearestImpostorAt(state.list, state.count, ctx.scene.camera.contract(), ctx.profile, p);
  return i < 0 ? -1 : (state.list[i * L.stride + L.drawable] ?? -1);
}
